"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { signOut, type SignOutOptions } from "../lib/utils/sign-out";

/**
 * React hook for sign out operations with Next.js router integration
 */
export function useSignOut() {
  const router = useRouter();

  const signOutWithRouter = useCallback(async (options: SignOutOptions = {}) => {
    const result = await signOut(options);
    
    // If navigation is requested and we have router, use it
    if (options.redirect && router && !options.serverSide) {
      try {
        const targetUrl = options.redirectTo || "/";
        router.push(targetUrl);
        console.log(`Router navigation to: ${targetUrl}`);
      } catch (error) {
        console.error("Router navigation failed, falling back to window.location:", error);
        // Fallback to window.location if router fails (with Chrome compatibility)
        if (typeof window !== 'undefined') {
          const targetUrl = options.redirectTo || "/";
          setTimeout(() => {
            window.location.href = targetUrl;
          }, 100);
        }
      }
    }
    
    return result;
  }, [router]);

  return {
    signOut: signOutWithRouter,
    signOutToHome: () => signOutWithRouter({ redirectTo: "/" }),
    signOutToLogin: () => signOutWithRouter({ redirectTo: "/login" }),
    signOutSilent: () => signOutWithRouter({ redirect: false }),
    signOutWithCleanup: () => signOutWithRouter({
      redirect: true,
      clearLocalStorage: true,
      clearSessionStorage: true,
      clearCookies: true,
      clearAllAuthCookies: true,
    }),
  };
}
